import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Select, Input, Button, Table, message, DatePicker } from 'antd';
import moment from 'moment';
import { EditOutlined } from '@ant-design/icons';
import { SelectEmployee } from '../SelectInput';
import { getRequest, postRequest } from '../../utils/APICall';

const { Option } = Select;

const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
];

const AddPayslipForm = ({ visible, onCancel, onOk }) => {
    const [entries, setEntries] = useState([]);
    const [payslipData, setPayslipData] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [employeeId, setEmployeeId] = useState(null);
    const { allEmployee } = useSelector((state) => state.employees);
    function removeDuplicatesByID(array) {
        const seen = new Set();
        return array.filter((item) => {
            if (seen.has(item.ID)) {
                return false;
            }
            seen.add(item.ID);
            return true;
        });
    }
    const filterData = removeDuplicatesByID(allEmployee)

    const predefinedTypes = ['Basic Salary', 'Bonus', 'Annual Bonus', 'Bonus Deduction', 'Deduction', 'Extra Benifits', 'Gross Salary', 'Income Tax', 'Leave Encash', 'Loan',
        'Medical', 'Other Benifits', 'Special Bonus', 'Text Deduction', 'Fuel Allownce', 'Advance Salary Deduction', 'Other'];

    const earningTypes = ['Basic Salary', 'Bonus', 'Annual Bonus', 'Extra Benifits', 'Gross Salary', 'Leave Encash', 'Medical', 'Other Benifits', 'Special Bonus', 'Fuel Allownce'];

    const [formData, setFormData] = useState({
        empName: '',
        chequeNo: '',
        month: '',
        year: new Date().getFullYear().toString(),
        type: '',
        earning: '',
        deduction: ''
    });

    const isAddButtonValid = () => {
        return (
            formData.type &&
            (formData.earning || formData.deduction)
        );
    };

    const isSubmitButtonValid = () => {
        return (
            employeeId &&
            formData.chequeNo &&
            formData.month &&
            formData.year &&
            entries.length > 0
        );
    };

    const isEarningDisabled = () => {
        if (!formData.type) return false;
        const typeLower = formData.type.toLowerCase();
        return typeLower.includes('deduction') || typeLower.includes('income tax') || typeLower.includes('loan');
    };

    const isDeductionDisabled = () => {
        if (!formData.type) return false;
        return earningTypes.includes(formData.type);
    };

    const resetForm = () => {
        setFormData({
            empName: '',
            chequeNo: '',
            month: '',
            year: '2025',
            type: '',
            earning: '',
            deduction: ''
        });
        setEntries([]);
        setEmployeeId(null);
        setEditingIndex(null);
    };

    const handleAddOrUpdate = () => {
        const { type, earning, deduction } = formData;
        const hasDuplicate = entries.some((entry, index) => {
            if (editingIndex !== null && index === editingIndex) return false;
            return entry.type === type;
        });

        if (hasDuplicate) {
            message.error('Duplicate salary head types are not allowed.');
            return;
        }
        const newEntry = {
            type,
            earning: earning || '0',
            deduction: deduction || '0',
        };

        if (editingIndex !== null) {
            const updatedEntries = [...entries];
            updatedEntries[editingIndex] = newEntry;
            setEntries(updatedEntries);
            setEditingIndex(null);
        } else {
            setEntries([...entries, newEntry]);
        }

        setFormData({ ...formData, type: '', earning: '', deduction: '' });
    };

    const showAddConfirm = () => {
        Modal.confirm({
            title: editingIndex !== null ? 'Confirm Update' : 'Confirm Add',
            content: editingIndex !== null ? 'Are you sure you want to update this payslip entry?' : 'Are you sure you want to add this payslip entry?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: handleAddOrUpdate,
        });
    };

    const handleSalaryStructure = async () => {
        if (!employeeId) {
            message.error('Please select an employee');
            return;
        }

        try {
            const res = await getRequest(`getStructurePayslip/${employeeId}`);
            if (res.success) {
                const data = res.data || [];

                const filtered = data.filter(item => item.EMP_ID == employeeId);

                if (filtered.length === 0) {
                    message.warning('No salary structure found for selected employee');
                    return;
                }

                const formatted = filtered.map(item => ({
                    type: item.SALARY_HEAD_NAME || 'Other',
                    earning: item.SALARY_HEAD_NAME?.toLowerCase().includes("deduction") ||
                        item.SALARY_HEAD_NAME?.toLowerCase().includes("loan") ||
                        item.SALARY_HEAD_NAME?.toLowerCase().includes("income tax")
                        ? '0' : item.SALARYHEADS_AMOUNT,
                    deduction:
                        item.SALARY_HEAD_NAME?.toLowerCase().includes("deduction") ||
                            item.SALARY_HEAD_NAME?.toLowerCase().includes("income tax") ||
                            item.SALARY_HEAD_NAME?.toLowerCase().includes("loan")
                            ? item.SALARYHEADS_AMOUNT : '0'
                }));

                setPayslipData(data);
                setEntries(formatted);
            } else {
                message.error(res.message || 'Failed to fetch payslips');
            }
        } catch (err) {
            console.error('Error fetching payslip data:', err);
            message.error('Error fetching payslip data');
        }
    };

    const handleSubmit = async () => {
        const { chequeNo, month, year } = formData;

        if (!isSubmitButtonValid()) {
            message.error('Please fill all required fields.');
            return;
        }

        const payload = {
            vempName: employeeId,
            vchequeno: chequeNo,
            vmonthName: month,
            vyear: year,
            vstatus: 0,
            entries: entries.map((entry) => ({
                vtype: entry.type,
                vearning: entry.earning,
                vdeduction: entry.deduction,
            })),
        };

        try {
            const res = await postRequest('addPayslips', payload);
            if (res?.success) {
                message.success(res.message);
                resetForm();
                onOk();
            } else {
                throw new Error(res.error || 'Something went wrong');
            }
        } catch (error) {
            message.error(error.message);
        }
    };

    const showSubmitConfirm = () => {
        Modal.confirm({
            title: 'Confirm Submission',
            content: 'Are you sure you want to create this payslip?',
            okText: 'Yes',
            cancelText: 'No',
            onOk: handleSubmit,
        });
    };

    const calculateTotals = () => {
        let totalIncome = 0;
        let totalDeduction = 0;
        entries.forEach((entry) => {
            const earningAmount = parseFloat(entry.earning || '0');
            const deductionAmount = parseFloat(entry.deduction || '0');
            if (!entry.type.toLowerCase().includes('deduction') && !entry.type.toLowerCase().includes('income tax') && !entry.type.toLowerCase().includes('loan')) {
                totalIncome += earningAmount;
            } else {
                totalDeduction += deductionAmount;
            }
        });

        const netTotal = totalIncome - totalDeduction;

        return { totalIncome, totalDeduction, netTotal };
    };

    const columns = [
        { title: 'Type', dataIndex: 'type' },
        { title: 'Earning', dataIndex: 'earning' },
        { title: 'Deduction', dataIndex: 'deduction' },
        {
            title: 'Action',
            render: (_, __, index) => (
                <Button
                    type="text"
                    icon={<EditOutlined />}
                    onClick={() => {
                        const entry = entries[index];
                        setFormData({
                            ...formData,
                            type: entry.type,
                            earning: entry.earning,
                            deduction: entry.deduction
                        });
                        setEditingIndex(index);
                    }}
                />
            )
        }
    ];

    return (
        <Modal
            title={
                <span className='flex justify-center text-xl font-bold'>
                    Add Payslip
                </span>
            }
            visible={visible}
            onCancel={() => {
                resetForm();
                onCancel();
            }}
            footer={null}
            width={1000}
        >
            <div className="flex flex-wrap gap-4 mb-4">
                <div className="flex flex-col w-full sm:w-[180px]">
                    <label className="mb-1 font-semibold">
                        Employee Name <span className="text-red-500">*</span>
                    </label>
                    <SelectEmployee
                        employeeList={filterData}
                        onChange={(value) => setEmployeeId(value)}
                        selectedEmployee={employeeId}
                        customStyle="w-full"
                        allLabel={{}}
                    />
                </div>
                <div className="flex flex-col w-full sm:w-[180px]">
                    <label className="mb-1 font-semibold">
                        Cheque No <span className="text-red-500">*</span>
                    </label>
                    <Input
                        placeholder="Enter cheque number"
                        value={formData.chequeNo}
                        className='h-10'
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({ ...formData, chequeNo: value });
                        }}
                    />
                </div>
                <div className="flex flex-col w-full sm:w-[180px]">
                    <label className="mb-1 font-semibold">
                        Month <span className="text-red-500">*</span>
                    </label>
                    <Select
                        placeholder="Select month"
                        value={formData.month}
                        className='h-10'
                        onChange={(val) => setFormData({ ...formData, month: val })}
                    >
                        {months.map((m) => (
                            <Option key={m.value} value={m.value}>
                                {m.label}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="flex flex-col w-full sm:w-[180px]">
                    <label className="mb-1 font-semibold">
                        Year <span className="text-red-500">*</span>
                    </label>
                    <DatePicker
                        picker="year"
                        className="w-full h-10"
                        placeholder="Select year"
                        value={formData.year ? moment(formData.year, 'YYYY') : null}
                        onChange={(date, dateString) =>
                            setFormData({ ...formData, year: dateString })
                        }
                    />
                </div>
                <div className='flex justify-center items-center'>
                    <label className="mb-1 font-semibold">
                    </label>
                    <Button
                        color='primary'
                        variant='filled'
                        onClick={handleSalaryStructure}
                        className="w-40 h-10 mt-6"> Search</Button>
                </div>
            </div>
            <div className="border p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 items-end">
                <div className="flex flex-col">
                    <label className="mb-1 font-semibold">Type</label>
                    <Select
                        mode="combobox"
                        showSearch
                        placeholder="Select or type type"
                        value={formData.type}
                        onChange={(val) => setFormData({ ...formData, type: val })}
                        className="w-full"
                    >
                        {predefinedTypes.map((t) => (
                            <Option key={t} value={t}>
                                {t}
                            </Option>
                        ))}
                    </Select>
                </div>
                <div className="flex flex-col">
                    <label className="mb-1 font-semibold">Earning</label>
                    <Input
                        placeholder="Enter earning"
                        value={formData.earning}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({ ...formData, earning: value });
                        }}
                        disabled={isEarningDisabled()}
                    />
                </div>
                <div className="flex flex-col">
                    <label className="mb-1 font-semibold">Deduction</label>
                    <Input
                        placeholder="Enter deduction"
                        value={formData.deduction}
                        onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, '');
                            setFormData({ ...formData, deduction: value });
                        }}
                        disabled={isDeductionDisabled()}
                    />
                </div>
                <div>
                    <Button
                        type="primary"
                        className="w-full bg-green-500"
                        onClick={showAddConfirm}
                        disabled={!isAddButtonValid()}
                    >
                        {editingIndex !== null ? 'Update' : 'Add'}
                    </Button>
                </div>
            </div>
            <Table
                columns={columns}
                dataSource={entries}
                pagination={false}
                rowKey={(_, index) => index}
            />
            <div className="flex justify-between mt-2 p-2 bg-gray-100">
                <span className='font-bold'>Total Income: <span className='text-lg font-bold'>{calculateTotals().totalIncome}</span></span>
                <span className='font-bold'>Total Deduction: <span className='text-lg font-bold'>{calculateTotals().totalDeduction}</span> </span>
                <span className='font-bold'>Net Total: <span className='text-lg font-bold'>{calculateTotals().netTotal}</span></span>
            </div>
            <div className="flex justify-end gap-2 mt-4">
                <Button className="bg-gray-300 hover:bg-gray-400" onClick={resetForm}>
                    New
                </Button>
                <Button
                    className="bg-green-500 text-white hover:bg-green-600"
                    onClick={showSubmitConfirm}
                    disabled={!isSubmitButtonValid()}
                >
                    Submit
                </Button>
            </div>
        </Modal>
    );
};

export default AddPayslipForm;